import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  type IntelLayerProviderInfo,
  type IntelModelCatalog,
  type IntelModelEntry,
  type IntelProvider,
} from '../lib/api';
import { useToasts } from '../lib/useToasts';
import {
  EVENT_CATEGORIES,
  FEATURE_FLAG_KEYS,
  NOTIFICATION_VARIABLE_GROUPS,
  ROLES,
  eventCategory,
  eventVerb,
  type EventCategory,
  type EventType,
  type FeatureFlag,
  type FeatureFlagKey,
  type NotificationTemplateKey,
  type Role,
} from '@air/shared';
import { formatRelative } from '../lib/format';

const ROLE_LABEL: Record<Role, string> = {
  employee: 'Employee',
  it_reviewer: 'IT Reviewer',
  legal_reviewer: 'Legal Reviewer',
  admin: 'Admin',
};

type AdminTab = 'metrics' | 'users' | 'notifications' | 'audit' | 'settings' | 'prompt';

const ADMIN_TABS: Array<{ id: AdminTab; label: string }> = [
  { id: 'metrics', label: 'Metrics' },
  { id: 'users', label: 'Users & Roles' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'audit', label: 'Audit Log' },
  { id: 'settings', label: 'Settings' },
  { id: 'prompt', label: 'AI Prompt' },
];

export function AdminPage() {
  const [tab, setTab] = useState<AdminTab>('metrics');
  return (
    <>
      <div className="page-headbar reveal">
        <div>
          <div className="page-overline">Admin</div>
          <h1 className="page-title">Settings &amp; metrics</h1>
          <p className="page-desc">
            Manage who can review, watch volume and turnaround, tune notification rules, edit the
            AI research prompt, and audit everything that happens in the workflow.
          </p>
        </div>
      </div>
      <div className="viewtoggle reveal reveal-1" style={{ marginBottom: 18, flexWrap: 'wrap' }}>
        {ADMIN_TABS.map((t) => (
          <button
            key={t.id}
            className={tab === t.id ? 'is-active' : ''}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'metrics' && <MetricsTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'notifications' && <NotificationsTab />}
      {tab === 'audit' && <AuditLogTab />}
      {tab === 'settings' && <SettingsTab />}
      {tab === 'prompt' && <AiPromptTab />}
    </>
  );
}

function ModelOptGroup({
  label,
  provider,
  configured,
  models,
  fallbackModel,
}: Readonly<{
  label: string;
  provider: 'anthropic' | 'google';
  configured: boolean;
  models: IntelModelEntry[];
  fallbackModel: string;
}>) {
  // If the catalog hasn't loaded (or returned empty), at least surface the
  // currently-saved model so the select shows the right thing.
  const list: IntelModelEntry[] = models.length > 0 ? models : [{ id: fallbackModel, name: fallbackModel }];
  return (
    <optgroup label={`${label}${configured ? '' : ' — API key missing'}`}>
      {list.map((m) => (
        <option key={m.id} value={`${provider}:${m.id}`} disabled={!configured}>
          {m.name === m.id ? m.id : `${m.name} (${m.id})`}
        </option>
      ))}
    </optgroup>
  );
}

function LayerModelPicker({
  layer,
  title,
  data,
  isLoading,
  error,
  models,
  modelsLoading,
  disabled,
  onChange,
}: Readonly<{
  layer: 'evidence' | 'threat';
  title: string;
  data: IntelLayerProviderInfo | undefined;
  isLoading: boolean;
  error: unknown;
  models: IntelModelCatalog | undefined;
  modelsLoading: boolean;
  disabled: boolean;
  onChange: (provider: IntelProvider, model: string) => void;
}>) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 260,
        border: '1px solid var(--border-subtle)',
        borderRadius: 10,
        padding: 14,
        background: '#FAFBFC',
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-navy)', marginBottom: 8 }}>
        {title}
      </div>
      {isLoading && <p className="muted" style={{ fontSize: 12 }}>Loading…</p>}
      {error ? <p className="field__error" style={{ fontSize: 12 }}>Could not load.</p> : null}
      {data && (
        <>
          <select
            value={`${data.provider}:${data.model}`}
            disabled={disabled || modelsLoading}
            onChange={(e) => {
              const [p, ...rest] = e.target.value.split(':');
              const m = rest.join(':');
              if (p === 'anthropic' || p === 'google') onChange(p, m);
            }}
            style={{ width: '100%' }}
          >
            <ModelOptGroup
              label="Anthropic Claude"
              provider="anthropic"
              configured={data.anthropicConfigured}
              models={models?.anthropic ?? []}
              fallbackModel={data.provider === 'anthropic' ? data.model : 'claude-opus-4-7'}
            />
            <ModelOptGroup
              label="Google Gemini"
              provider="google"
              configured={data.googleConfigured}
              models={models?.google ?? []}
              fallbackModel={data.provider === 'google' ? data.model : 'gemini-2.5-pro'}
            />
          </select>
          {modelsLoading && (
            <p className="muted" style={{ fontSize: 11, marginTop: 6, marginBottom: 0 }}>
              Loading available models…
            </p>
          )}
          <p style={{ fontSize: 11, color: 'var(--color-steel)', marginTop: 8, marginBottom: 0 }}>
            {layer === 'evidence'
              ? 'Tip: extraction-focused models (e.g. Gemini Pro) keep hallucinations low here.'
              : 'Tip: stronger reasoning models (e.g. Claude Opus) produce richer threat findings.'}
          </p>
        </>
      )}
    </div>
  );
}

const FLAG_DESCRIPTIONS: Record<FeatureFlagKey, { title: string; sub: string }> = {
  vendor_intel_visible_to_employee: {
    title: 'Show Company Background to Employees',
    sub: 'When ON, employees see the AI-generated vendor intel card on the detail page for their own requests.',
  },
  vendor_intel_visible_to_it_reviewer: {
    title: 'Show Company Background to IT Reviewers',
    sub: 'When ON, IT reviewers see the AI-generated vendor intel card on every request they review.',
  },
  vendor_intel_visible_to_legal_reviewer: {
    title: 'Show Company Background to Legal Reviewers',
    sub: 'When ON, Legal reviewers see the AI-generated vendor intel card — including the Compliance matrix.',
  },
};

function SettingsTab() {
  const qc = useQueryClient();
  const toasts = useToasts();
  const flags = useQuery({ queryKey: ['admin-feature-flags'], queryFn: api.adminListFeatureFlags });
  const evidenceProvider = useQuery({
    queryKey: ['admin-intel-provider', 'evidence'],
    queryFn: () => api.adminGetLayerProvider('evidence'),
  });
  const threatProvider = useQuery({
    queryKey: ['admin-intel-provider', 'threat'],
    queryFn: () => api.adminGetLayerProvider('threat'),
  });
  const models = useQuery({
    queryKey: ['admin-intel-models'],
    queryFn: api.adminListIntelModels,
    staleTime: 5 * 60_000,
  });
  const sla = useQuery({ queryKey: ['admin-sla'], queryFn: api.adminGetSla });

  const [slaDraft, setSlaDraft] = useState<{ itHours: string; legalHours: string; termLabel: string }>({
    itHours: '',
    legalHours: '',
    termLabel: '',
  });

  const setSla = useMutation({
    mutationFn: (patch: { itHours?: number; legalHours?: number; termLabel?: string }) =>
      api.adminSetSla(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-sla'] });
      void qc.invalidateQueries({ queryKey: ['public-config'] });
      void qc.invalidateQueries({ queryKey: ['queue-bundle', 'it'] });
      void qc.invalidateQueries({ queryKey: ['queue-bundle', 'legal'] });
      setSlaDraft({ itHours: '', legalHours: '', termLabel: '' });
      toasts.push('Saved.');
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Could not save settings.', 'err');
    },
  });

  const slaItDirty =
    slaDraft.itHours !== '' && Number.parseInt(slaDraft.itHours, 10) !== sla.data?.itHours;
  const slaLegalDirty =
    slaDraft.legalHours !== '' && Number.parseInt(slaDraft.legalHours, 10) !== sla.data?.legalHours;
  const slaTermDirty =
    slaDraft.termLabel !== '' && slaDraft.termLabel.trim() !== sla.data?.termLabel;
  const slaDirty = slaItDirty || slaLegalDirty || slaTermDirty;

  function saveSla(): void {
    const patch: { itHours?: number; legalHours?: number; termLabel?: string } = {};
    if (slaItDirty) patch.itHours = Number.parseInt(slaDraft.itHours, 10);
    if (slaLegalDirty) patch.legalHours = Number.parseInt(slaDraft.legalHours, 10);
    if (slaTermDirty) patch.termLabel = slaDraft.termLabel.trim();
    setSla.mutate(patch);
  }

  const setFlag = useMutation({
    mutationFn: ({ key, value }: { key: FeatureFlagKey; value: boolean }) =>
      api.adminSetFeatureFlag(key, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-feature-flags'] });
      void qc.invalidateQueries({ queryKey: ['feature-flags'] });
      toasts.push('Updated.');
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Update failed.', 'err');
    },
  });

  const setLayerProvider = useMutation({
    mutationFn: ({
      layer,
      provider: p,
      model,
    }: {
      layer: 'evidence' | 'threat';
      provider: IntelProvider;
      model?: string;
    }) => api.adminSetLayerProvider(layer, p, model),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-intel-provider', vars.layer] });
      toasts.push(`Layer ${vars.layer === 'evidence' ? '1 (Evidence)' : '2 (Threat)'} model updated.`);
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Could not change model.', 'err');
    },
  });

  const refreshModels = useMutation({
    mutationFn: () => api.adminRefreshIntelModels(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-intel-models'] });
      toasts.push('Model catalog refreshed.');
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Could not refresh catalog.', 'err');
    },
  });

  const byKey = new Map<FeatureFlagKey, FeatureFlag>();
  for (const f of flags.data ?? []) byKey.set(f.key, f);

  return (
    <div className="reveal reveal-2" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 760 }}>
      <div className="card card--padded">
        <h3 className="card__title" style={{ marginTop: 0, marginBottom: 6 }}>
          Review turnaround targets
        </h3>
        <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 14 }}>
          How long a request can sit Open before it's flagged red on the reviewer queue. You can
          also rename the term — by default the queue uses the corporate word "SLA"; switch it to
          plain English like "Turnaround" or "Response time" if reviewers push back.
        </p>
        {sla.isLoading && <p className="muted">Loading…</p>}
        {sla.error && <p className="field__error">Could not load these settings.</p>}
        {sla.data && (
          <>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 160 }}>
                <label className="field__label" htmlFor="sla-it-hours">
                  IT review (hours)
                </label>
                <input
                  id="sla-it-hours"
                  type="number"
                  min={1}
                  max={720}
                  step={1}
                  value={slaDraft.itHours !== '' ? slaDraft.itHours : String(sla.data.itHours)}
                  onChange={(e) => setSlaDraft((d) => ({ ...d, itHours: e.target.value }))}
                />
              </div>
              <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 160 }}>
                <label className="field__label" htmlFor="sla-legal-hours">
                  Legal review (hours)
                </label>
                <input
                  id="sla-legal-hours"
                  type="number"
                  min={1}
                  max={720}
                  step={1}
                  value={slaDraft.legalHours !== '' ? slaDraft.legalHours : String(sla.data.legalHours)}
                  onChange={(e) => setSlaDraft((d) => ({ ...d, legalHours: e.target.value }))}
                />
              </div>
              <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 180 }}>
                <label className="field__label" htmlFor="sla-term-label">
                  Display label
                </label>
                <input
                  id="sla-term-label"
                  type="text"
                  maxLength={40}
                  placeholder="SLA"
                  value={slaDraft.termLabel !== '' ? slaDraft.termLabel : sla.data.termLabel}
                  onChange={(e) => setSlaDraft((d) => ({ ...d, termLabel: e.target.value }))}
                />
              </div>
              <button
                className="btn btn--primary btn--sm"
                disabled={setSla.isPending || !slaDirty}
                onClick={saveSla}
              >
                {setSla.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
            <p style={{ fontSize: 11, color: 'var(--color-steel)', marginTop: 10, marginBottom: 0 }}>
              Hours clamp to 1–720. The display label replaces the word "SLA" everywhere on the queue
              page (stat card, row badge, page header). Queue pages pick up changes within 30
              seconds. Defaults: IT 72h, Legal 120h, label "SLA".
            </p>
          </>
        )}
      </div>

      <div className="card card--padded">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <h3 className="card__title" style={{ margin: 0 }}>
            AI models for the vendor-research pipeline
          </h3>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => refreshModels.mutate()}
            disabled={refreshModels.isPending}
            title="Re-fetch the model catalog from Anthropic and Google"
          >
            {refreshModels.isPending ? 'Refreshing…' : '↻ Refresh models'}
          </button>
        </div>
        <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 14 }}>
          The pipeline runs in two AI layers: <strong>Layer 1 — Evidence</strong> extracts
          verifiable facts (Gemini is the recommended default); <strong>Layer 2 — Threat</strong>
          maps those facts to operational risk findings (Claude is the recommended default). Layer 3
          is a deterministic code-defined rule engine — no model selection needed.
        </p>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <LayerModelPicker
            layer="evidence"
            title="Layer 1 — Evidence (extract facts)"
            data={evidenceProvider.data}
            isLoading={evidenceProvider.isLoading}
            error={evidenceProvider.error}
            models={models.data}
            modelsLoading={models.isLoading}
            disabled={setLayerProvider.isPending}
            onChange={(provider, model) =>
              setLayerProvider.mutate({ layer: 'evidence', provider, model })
            }
          />
          <LayerModelPicker
            layer="threat"
            title="Layer 2 — Threat (operational risk)"
            data={threatProvider.data}
            isLoading={threatProvider.isLoading}
            error={threatProvider.error}
            models={models.data}
            modelsLoading={models.isLoading}
            disabled={setLayerProvider.isPending}
            onChange={(provider, model) =>
              setLayerProvider.mutate({ layer: 'threat', provider, model })
            }
          />
        </div>
        {models.error && (
          <p className="field__error" style={{ fontSize: 11, marginTop: 10 }}>
            Could not list models from one or both providers. The currently-saved model is still
            used; click "Refresh models" to retry.
          </p>
        )}
        <p style={{ fontSize: 11, color: 'var(--color-steel)', marginTop: 8, marginBottom: 0 }}>
          API keys are set as environment variables on the server
          (<span className="mono">ANTHROPIC_API_KEY</span>, <span className="mono">GOOGLE_API_KEY</span>).
          Whole providers are disabled if their key is missing.
        </p>
      </div>

      <div className="card card--padded">
        <h3 className="card__title" style={{ marginTop: 0, marginBottom: 6 }}>
          Company Background visibility
        </h3>
        <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 18 }}>
          Admins always see the AI-generated Company Background card. Use these toggles to make it
          visible to other roles. All are OFF by default.
        </p>

        {flags.isLoading && <p className="muted">Loading…</p>}
        {flags.error && <p className="field__error">Could not load feature flags.</p>}

        {flags.data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {FEATURE_FLAG_KEYS.map((key) => {
              const flag = byKey.get(key);
              const meta = FLAG_DESCRIPTIONS[key];
              const value = flag?.value ?? false;
              const pending =
                setFlag.isPending && setFlag.variables?.key === key;
              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    gap: 16,
                    alignItems: 'center',
                    padding: 14,
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 10,
                    background: '#FAFBFC',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--color-navy)' }}>
                      {meta.title}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-steel)', marginTop: 4 }}>
                      {meta.sub}
                    </div>
                    {flag?.updatedByName && (
                      <div style={{ fontSize: 11, color: 'var(--color-steel)', marginTop: 6 }}>
                        Last changed by {flag.updatedByName} · {formatRelative(flag.updatedAt)}
                      </div>
                    )}
                  </div>
                  <label className="toggle" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={value}
                      disabled={pending}
                      onChange={(e) => setFlag.mutate({ key, value: e.target.checked })}
                    />
                    <span className="toggle__track" />
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function UsersTab() {
  const qc = useQueryClient();
  const toasts = useToasts();
  const users = useQuery({ queryKey: ['admin-users'], queryFn: api.listUsers });
  const [resetResult, setResetResult] = useState<{ email: string; password?: string; mode: 'email' | 'temp_password' } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; email: string } | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('employee');
  const [tempPw, setTempPw] = useState<{ email: string; password: string } | null>(null);

  const create = useMutation({
    mutationFn: api.createUser,
    onSuccess: (u) => {
      setTempPw({ email: u.email, password: u.tempPassword });
      setEmail('');
      setFullName('');
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Create failed.', 'err');
    },
  });

  const patch = useMutation({
    mutationFn: ({ id, ...rest }: { id: string; fullName?: string; role?: Role; isActive?: boolean }) =>
      api.patchUser(id, rest),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Update failed.', 'err');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => {
      toasts.push('User deleted.');
      setPendingDelete(null);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Delete failed.', 'err');
      setPendingDelete(null);
    },
  });

  const resetPw = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: 'email' | 'temp_password' }) =>
      api.adminResetPassword(id, mode),
    onSuccess: (data) => {
      setResetResult({ email: data.email, password: data.tempPassword, mode: data.mode });
      if (data.mode === 'email') {
        toasts.push(`Reset link emailed to ${data.email}.`);
      }
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Reset failed.', 'err');
    },
  });

  function commitName(id: string) {
    const trimmed = nameDraft.trim();
    if (trimmed.length === 0) {
      setEditingNameId(null);
      return;
    }
    patch.mutate({ id, fullName: trimmed });
    setEditingNameId(null);
  }

  return (
    <div className="row--2col reveal reveal-2">
      <div className="card">
        <div className="card__header">
          <h3 className="card__title">Users</h3>
        </div>
        <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.data?.map((u) => (
                <tr key={u.id}>
                  <td>
                    {editingNameId === u.id ? (
                      <input
                        type="text"
                        autoFocus
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onBlur={() => commitName(u.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitName(u.id);
                          if (e.key === 'Escape') setEditingNameId(null);
                        }}
                        style={{ width: '100%', fontSize: 13, padding: '4px 8px' }}
                      />
                    ) : (
                      <span
                        onClick={() => {
                          setNameDraft(u.fullName);
                          setEditingNameId(u.id);
                        }}
                        title="Click to edit"
                        style={{ cursor: 'pointer' }}
                      >
                        {u.fullName}
                      </span>
                    )}
                  </td>
                  <td>{u.email}</td>
                  <td>
                    <select
                      value={u.role}
                      onChange={(e) => patch.mutate({ id: u.id, role: e.target.value as Role })}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={u.isActive}
                        onChange={(e) => patch.mutate({ id: u.id, isActive: e.target.checked })}
                      />
                      <span className="toggle__track" />
                    </label>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn--secondary btn--sm"
                        title="Email a reset link"
                        disabled={!u.isActive || resetPw.isPending}
                        onClick={() => resetPw.mutate({ id: u.id, mode: 'email' })}
                      >
                        Email reset
                      </button>
                      <button
                        className="btn btn--ghost btn--sm"
                        title="Generate a temporary password"
                        disabled={!u.isActive || resetPw.isPending}
                        onClick={() => resetPw.mutate({ id: u.id, mode: 'temp_password' })}
                      >
                        Temp PW
                      </button>
                      <button
                        className="btn btn--danger btn--sm"
                        title="Delete user"
                        onClick={() => setPendingDelete({ id: u.id, email: u.email })}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card card--padded">
        <h3 className="card__title" style={{ marginBottom: 14 }}>Add a user</h3>
        <div className="field">
          <label className="field__label">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@vin.com"
          />
        </div>
        <div className="field" style={{ marginTop: 10 }}>
          <label className="field__label">Full name</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div className="field" style={{ marginTop: 10 }}>
          <label className="field__label">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </div>
        <button
          className="btn btn--primary"
          style={{ marginTop: 14 }}
          disabled={!email || !fullName || create.isPending}
          onClick={() => create.mutate({ email, fullName, role })}
        >
          {create.isPending ? 'Creating…' : 'Create user'}
        </button>

        {tempPw && (
          <div className="alert alert--info" style={{ marginTop: 14, display: 'block' }}>
            <strong>One-time password for {tempPw.email}</strong>
            <p style={{ marginTop: 6 }} className="mono">
              {tempPw.password}
            </p>
            <p style={{ fontSize: 12, marginTop: 6 }}>
              Share this securely. The user will be forced to change it on first sign-in.
            </p>
          </div>
        )}

        {resetResult?.password && (
          <div className="alert alert--info" style={{ marginTop: 14, display: 'block' }}>
            <strong>Temporary password for {resetResult.email}</strong>
            <p style={{ marginTop: 6 }} className="mono">
              {resetResult.password}
            </p>
            <p style={{ fontSize: 12, marginTop: 6 }}>
              Share this securely. The user will be forced to change it on next sign-in.
            </p>
            <button
              className="btn btn--ghost btn--sm"
              style={{ marginTop: 8 }}
              onClick={() => setResetResult(null)}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {pendingDelete && (
        <ConfirmDialog
          title="Delete user?"
          body={
            <>
              You're about to delete <strong>{pendingDelete.email}</strong>. This is hard-delete — only
              works if the user has no submitted requests or review actions. If they have history,
              deactivate them instead.
            </>
          }
          confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
          confirmKind="danger"
          disabled={remove.isPending}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => remove.mutate(pendingDelete.id)}
        />
      )}
    </div>
  );
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  confirmKind,
  disabled,
  onCancel,
  onConfirm,
}: Readonly<{
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  confirmKind: 'primary' | 'danger';
  disabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}>) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,26,114,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={onCancel}
    >
      <div
        className="card card--padded"
        style={{ maxWidth: 440, width: '90%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="card__title" style={{ marginBottom: 8 }}>{title}</h3>
        <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--color-navy)', margin: '0 0 18px' }}>
          {body}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn--ghost" onClick={onCancel} disabled={disabled}>
            Cancel
          </button>
          <button
            className={`btn btn--${confirmKind}`}
            onClick={onConfirm}
            disabled={disabled}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricsTab() {
  const m = useQuery({ queryKey: ['admin-metrics'], queryFn: api.metrics });
  const sla = useQuery({ queryKey: ['admin-sla'], queryFn: api.adminGetSla });
  const term = sla.data?.termLabel ?? 'SLA';
  if (m.isLoading) return <p className="muted">Loading…</p>;
  if (!m.data) return <p className="field__error">Could not load metrics.</p>;
  const data = m.data;
  const pendingIt = data.byStatus.pending_it_review ?? 0;
  const pendingLegal = data.byStatus.pending_legal_review ?? 0;
  const pending = pendingIt + pendingLegal;
  const approved = (data.byStatus.approved ?? 0) + (data.byStatus.approved_with_conditions ?? 0);
  const rejected = data.byStatus.rejected ?? 0;
  const approvalRate = data.total === 0 ? 0 : Math.round((approved / data.total) * 100);

  const maxWeekly = data.weekly.length === 0 ? 1 : Math.max(...data.weekly.map((w) => w.count), 1);

  return (
    <div className="reveal reveal-2" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div className="stats">
        <div className="stat stat--accent">
          <div className="stat__label">Total Requests</div>
          <div className="stat__value">{data.total}</div>
          <div className="stat__delta">all time</div>
        </div>
        <div className="stat stat--warning">
          <div className="stat__label">Currently In Review</div>
          <div className="stat__value">{pending}</div>
          <div className="stat__delta">{pendingIt} at IT, {pendingLegal} at Legal</div>
        </div>
        <div className="stat stat--success">
          <div className="stat__label">Approved</div>
          <div className="stat__value">{approved}</div>
          <div className="stat__delta">{approvalRate}% approval rate</div>
        </div>
        <div className="stat stat--danger">
          <div className="stat__label">Rejected</div>
          <div className="stat__value">{rejected}</div>
          <div className="stat__delta">includes IT and Legal denials</div>
        </div>
      </div>

      <div className="row--2col">
        <div className="card card--padded">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
            <h3 className="card__title" style={{ margin: 0 }}>Submissions per week</h3>
            <span className="muted" style={{ fontSize: 11 }}>Last 8 weeks</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 180, padding: '0 4px' }}>
            {data.weekly.map((w, i) => {
              const isCurrent = i === data.weekly.length - 1;
              const height = `${(w.count / maxWeekly) * 140}px`;
              const label = isCurrent ? 'This wk' : `Wk -${data.weekly.length - 1 - i}`;
              return (
                <div
                  key={w.weekStart}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}
                >
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--color-navy)' }}>{w.count}</div>
                  <div
                    style={{
                      width: '100%',
                      height,
                      background: isCurrent ? 'var(--color-navy)' : 'var(--color-light-steel)',
                      borderRadius: '6px 6px 0 0',
                      transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  />
                  <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--color-steel)', letterSpacing: '.04em' }}>
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
            Volume per ISO week. Current week is highlighted.
          </p>
        </div>

        <div className="card card--padded">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
            <h3 className="card__title" style={{ margin: 0 }}>Reviewer {term.toLowerCase() === 'sla' ? `${term}s` : term}</h3>
            <span className="muted" style={{ fontSize: 11 }}>30-day rolling</span>
          </div>
          {data.reviewers.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>No active reviewers.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {data.reviewers.map((r) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className="comment__avatar" style={{ width: 32, height: 32, fontSize: 12 }}>
                    {initialsFor(r.fullName)}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--color-navy)' }}>{r.fullName}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-steel)' }}>
                      {r.role === 'it_reviewer' ? 'IT Reviewer' : 'Legal Reviewer'}
                    </div>
                  </div>
                  <SlaCol label="Reviewed" value={r.reviewedCount} />
                  <SlaCol label="Median" value={formatDuration(r.medianSeconds)} />
                  <SlaCol label="Open" value={r.openCount} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="row--2col">
        <div className="stat">
          <div className="stat__label">Median IT time</div>
          <div className="stat__value">{formatDuration(data.medianItSeconds)}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Median Legal time</div>
          <div className="stat__value">{formatDuration(data.medianLegalSeconds)}</div>
        </div>
      </div>
    </div>
  );
}

function SlaCol({ label, value }: Readonly<{ label: string; value: string | number }>) {
  return (
    <div style={{ textAlign: 'right', minWidth: 64 }}>
      <div style={{ fontSize: 10, color: 'var(--color-steel)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 800 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--color-navy)' }}>{value}</div>
    </div>
  );
}

function initialsFor(name: string): string {
  return (
    name
      .replace(/[^A-Za-z ]/g, '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]!.toUpperCase())
      .join('') || '·'
  );
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
}

// Default preview variable values used in the live editor preview. The
// admin can override any of these in the Variables sidebar — overrides
// only affect the preview, not the saved template.
const DEFAULT_PREVIEW_VARS: Record<string, string> = {
  ticket_number: 'AIR-2026-0007',
  vendor: 'Lovable',
  software_name: 'Lovable',
  category: 'Productivity',
  request_url: 'https://airegistry.software/requests/sample',
  intended_use: 'Generate internal tooling prototypes',
  submitter_name: 'John Dean',
  submitter_first_name: 'John',
  submitter_email: 'john@vetvision.org',
  reviewer_name: 'Jamie Reviewer',
  reviewer_role: 'IT',
  reviewer_email: 'it@vetvision.org',
  review_comment: 'Looks good — please confirm data residency.',
  review_conditions: 'No PHI; review re-evaluated in 6 months.',
  submitter_response: 'We confirmed all data stays in US-East.',
  decision: 'Approved with conditions',
  from_status: 'pending_legal_review',
  to_status: 'approved_with_conditions',
  sla_hours: '72',
  sla_term_label: 'SLA',
  hours_overdue: '4',
};

const VARIABLE_GROUP_LABELS: Record<keyof typeof NOTIFICATION_VARIABLE_GROUPS, string> = {
  Request: 'Request',
  Submitter: 'Submitter',
  Reviewer: 'Reviewer',
  Comments: 'Comments',
  Status: 'Status',
  SLA: 'SLA',
};

const VARIABLE_PRETTY_LABEL: Record<string, string> = {
  ticket_number: 'Ticket number',
  vendor: 'Vendor',
  software_name: 'Software name',
  category: 'Category',
  request_url: 'Request URL',
  intended_use: 'Intended use',
  submitter_name: 'Submitter name',
  submitter_first_name: 'Submitter first name',
  submitter_email: 'Submitter email',
  reviewer_name: 'Reviewer name',
  reviewer_role: 'Reviewer role (IT/Legal)',
  reviewer_email: 'Reviewer email',
  review_comment: 'Review comment',
  review_conditions: 'Review conditions',
  submitter_response: 'Submitter response',
  decision: 'Decision label',
  from_status: 'From status',
  to_status: 'To status',
  sla_hours: 'SLA hours',
  sla_term_label: 'SLA term label',
  hours_overdue: 'Hours overdue',
};

function NotificationsTab() {
  const [editing, setEditing] = useState<NotificationTemplateKey | null>(null);
  const templates = useQuery({
    queryKey: ['admin-notification-templates'],
    queryFn: () => api.adminListNotificationTemplates(),
  });

  if (editing) {
    return (
      <NotificationTemplateEditor
        templateKey={editing}
        onBack={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="card reveal reveal-2">
      <div className="card__header">
        <h3 className="card__title">Email notification rules</h3>
        <span className="muted" style={{ fontSize: 11 }}>
          Sent via Resend. Click a row to edit the subject + body or send a test email.
        </span>
      </div>
      <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
        {templates.isLoading && <p className="muted" style={{ padding: 18 }}>Loading…</p>}
        {templates.error ? (
          <p className="field__error" style={{ padding: 18 }}>Could not load templates.</p>
        ) : null}
        {templates.data && (
          <table className="table">
            <thead>
              <tr>
                <th>Event</th>
                <th style={{ width: 240 }}>Recipient</th>
                <th style={{ width: 200 }}>Last edited</th>
                <th style={{ width: 100 }}>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {templates.data.map((t) => (
                <tr
                  key={t.key}
                  onClick={() => setEditing(t.key)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <strong>{t.label}</strong>
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {t.triggerDescription}
                    </div>
                  </td>
                  <td>{t.recipientLabel}</td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {t.isDefault ? (
                      'Default'
                    ) : (
                      <>
                        {t.updatedByName ?? '—'} · {formatRelative(t.updatedAt)}
                      </>
                    )}
                  </td>
                  <td>
                    <label className="toggle" aria-label={`${t.label} enabled`}>
                      <input type="checkbox" checked={t.enabled} readOnly />
                      <span className="toggle__track" />
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Editor ────────────────────────────────────────────────────────────────

function NotificationTemplateEditor({
  templateKey,
  onBack,
}: Readonly<{ templateKey: NotificationTemplateKey; onBack: () => void }>) {
  const qc = useQueryClient();
  const toasts = useToasts();
  const tplQuery = useQuery({
    queryKey: ['admin-notification-template', templateKey],
    queryFn: () => api.adminGetNotificationTemplate(templateKey),
  });

  const [subjectDraft, setSubjectDraft] = useState<string | null>(null);
  const [bodyDraft, setBodyDraft] = useState<string | null>(null);
  const [enabledDraft, setEnabledDraft] = useState<boolean | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [varsOpen, setVarsOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('notif-vars-open') !== '0';
    } catch {
      return true;
    }
  });
  const [previewVars, setPreviewVars] = useState<Record<string, string>>(DEFAULT_PREVIEW_VARS);
  const [testEmail, setTestEmail] = useState('');

  // Track last-focused field so variable-chip clicks insert in the right place.
  const lastFocusedRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  function persistVarsOpen(open: boolean): void {
    setVarsOpen(open);
    try {
      window.localStorage.setItem('notif-vars-open', open ? '1' : '0');
    } catch {
      // ignore
    }
  }

  const save = useMutation({
    mutationFn: (patch: { subject?: string; body?: string; enabled?: boolean }) =>
      api.adminSaveNotificationTemplate(templateKey, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-notification-template', templateKey] });
      void qc.invalidateQueries({ queryKey: ['admin-notification-templates'] });
      setSubjectDraft(null);
      setBodyDraft(null);
      setEnabledDraft(null);
      toasts.push('Template saved.');
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Could not save.', 'err');
    },
  });

  const reset = useMutation({
    mutationFn: () => api.adminResetNotificationTemplate(templateKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-notification-template', templateKey] });
      void qc.invalidateQueries({ queryKey: ['admin-notification-templates'] });
      setSubjectDraft(null);
      setBodyDraft(null);
      toasts.push('Template reset to default.');
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Could not reset.', 'err');
    },
  });

  const test = useMutation({
    mutationFn: () => api.adminTestNotificationTemplate(templateKey, testEmail, previewVars),
    onSuccess: () => {
      toasts.push(`Test email queued to ${testEmail}.`);
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Could not send test.', 'err');
    },
  });

  if (tplQuery.isLoading) return <p className="muted" style={{ padding: 18 }}>Loading template…</p>;
  if (tplQuery.error || !tplQuery.data) {
    return <p className="field__error" style={{ padding: 18 }}>Could not load this template.</p>;
  }

  const tpl = tplQuery.data;
  const subjectValue = subjectDraft ?? tpl.subjectTemplate;
  const bodyValue = bodyDraft ?? tpl.bodyTemplate;
  const enabledValue = enabledDraft ?? tpl.enabled;
  const dirty =
    (subjectDraft !== null && subjectDraft !== tpl.subjectTemplate) ||
    (bodyDraft !== null && bodyDraft !== tpl.bodyTemplate) ||
    (enabledDraft !== null && enabledDraft !== tpl.enabled);

  function discard(): void {
    setSubjectDraft(null);
    setBodyDraft(null);
    setEnabledDraft(null);
  }

  function commitSave(): void {
    const patch: { subject?: string; body?: string; enabled?: boolean } = {};
    if (subjectDraft !== null && subjectDraft !== tpl.subjectTemplate) patch.subject = subjectDraft;
    if (bodyDraft !== null && bodyDraft !== tpl.bodyTemplate) patch.body = bodyDraft;
    if (enabledDraft !== null && enabledDraft !== tpl.enabled) patch.enabled = enabledDraft;
    save.mutate(patch);
  }

  function insertAtCursor(token: string, target?: 'subject' | 'body'): void {
    const explicit = target === 'subject' ? subjectRef.current : target === 'body' ? bodyRef.current : null;
    const el = explicit ?? lastFocusedRef.current ?? bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const before = el.value.slice(0, start);
    const after = el.value.slice(end);
    const next = `${before}${token}${after}`;
    if (el === subjectRef.current) {
      setSubjectDraft(next);
    } else {
      setBodyDraft(next);
    }
    // Restore cursor after React re-renders.
    setTimeout(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  }

  const subjectLen = subjectValue.length;
  const subjectTooLong = subjectLen > 78;
  const previewWidth = previewMode === 'mobile' ? 360 : 600;
  const renderedSubject = renderPreview(subjectValue, previewVars, 'text').rendered;

  return (
    <div className="tpl-editor reveal reveal-2">
      <div className="tpl-editor__head">
        <button type="button" className="btn btn--ghost btn--sm" onClick={onBack}>
          ← Back to rules
        </button>
        <div className="tpl-editor__title">
          <strong>{tpl.label}</strong>
          <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
            {tpl.triggerDescription}
          </span>
        </div>
        <div className="tpl-editor__actions">
          <label className="toggle" title="Send this notification when its trigger fires">
            <input
              type="checkbox"
              checked={enabledValue}
              onChange={(e) => setEnabledDraft(e.target.checked)}
            />
            <span className="toggle__track" />
            <span style={{ fontSize: 11, marginLeft: 6 }}>Enabled</span>
          </label>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={reset.isPending || tpl.isDefault}
            onClick={() => {
              if (window.confirm('Reset this template to the built-in default? Your current text will be overwritten.')) {
                reset.mutate();
              }
            }}
          >
            {reset.isPending ? 'Resetting…' : 'Reset to default'}
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={!dirty || save.isPending}
            onClick={discard}
          >
            Discard
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={!dirty || save.isPending || subjectTooLong}
            onClick={commitSave}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="tpl-editor__meta muted" style={{ fontSize: 12 }}>
        Event: <span className="mono">{tpl.key}</span> · Recipient: {tpl.recipientLabel} · Template ID:{' '}
        <span className="mono">{tpl.key}.eml</span>
      </div>

      <div className={`tpl-editor__grid ${varsOpen ? 'tpl-editor__grid--vars-open' : ''}`}>
        {/* Pane 1 — Edit */}
        <div className="tpl-pane">
          <h4 className="tpl-pane__title">Edit</h4>
          <div className="field">
            <label className="field__label" htmlFor="tpl-subject">Subject</label>
            <input
              id="tpl-subject"
              ref={subjectRef}
              type="text"
              value={subjectValue}
              onChange={(e) => setSubjectDraft(e.target.value)}
              onFocus={() => { lastFocusedRef.current = subjectRef.current; }}
              className="mono"
              style={{ width: '100%' }}
            />
            <div style={{ fontSize: 11, marginTop: 4, color: subjectTooLong ? 'var(--color-red)' : 'var(--color-steel)' }}>
              {subjectLen} chars {subjectTooLong && '· over 78-char deliverability hint'}
            </div>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="tpl-body">Body</label>
            <textarea
              id="tpl-body"
              ref={bodyRef}
              value={bodyValue}
              onChange={(e) => setBodyDraft(e.target.value)}
              onFocus={() => { lastFocusedRef.current = bodyRef.current; }}
              rows={18}
              className="mono"
              style={{ width: '100%', resize: 'vertical', minHeight: 280 }}
              spellCheck={false}
            />
          </div>

          <div className="tpl-snippets">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => insertAtCursor('{{button:View request:request_url}}', 'body')}
            >
              + Button
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => insertAtCursor('\n\n— AI Software Registry', 'body')}
            >
              + Signature
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => insertAtCursor('Hi {{submitter_first_name}},\n\n', 'body')}
            >
              + Greeting
            </button>
          </div>
        </div>

        {/* Pane 2 — Preview */}
        <div className="tpl-pane">
          <div className="tpl-preview-head">
            <h4 className="tpl-pane__title">Live preview</h4>
            <div className="tpl-preview-toggle" role="tablist" aria-label="Preview width">
              {(['desktop', 'mobile'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  role="tab"
                  aria-selected={previewMode === m}
                  className={previewMode === m ? 'is-active' : ''}
                  onClick={() => setPreviewMode(m)}
                >
                  {m === 'desktop' ? 'Desktop' : 'Mobile'}
                </button>
              ))}
            </div>
          </div>
          <div className="tpl-email-shell">
            <div className="tpl-email-chrome">
              <div><span className="muted">From:</span> AI Software Registry &lt;{tpl.recipientLabel.toLowerCase().replace(/\s+/g,'-')}@vetvision.org&gt;</div>
              <div><span className="muted">To:</span> {tpl.recipientLabel}</div>
              <div>
                <span className="muted">Subject:</span>{' '}
                <strong>{renderedSubject || <span className="muted">(empty)</span>}</strong>
              </div>
            </div>
            <div className="tpl-email" style={{ maxWidth: previewWidth }}>
              <div className="tpl-email__header">AI Software Registry</div>
              <div
                className="tpl-email__body"
                // renderPreview returns DOM-safe HTML built from escaped text
                // + sanctioned tag/attribute injection (button + variable chips).
                dangerouslySetInnerHTML={{ __html: renderPreview(bodyValue, previewVars, 'html').rendered }}
              />
              <div className="tpl-email__footer">
                Internal tool — VIN staff only. ·{' '}
                <a href="#" style={{ color: 'inherit' }}>Notification preferences</a> ·{' '}
                <a href="#" style={{ color: 'inherit' }}>Unsubscribe</a>
              </div>
            </div>
          </div>

          <div className="tpl-test">
            <h4 className="tpl-pane__title">Send test</h4>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@vetvision.org"
                className="mono"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={!testEmail || test.isPending}
                onClick={() => test.mutate()}
              >
                {test.isPending ? 'Queuing…' : 'Send test'}
              </button>
            </div>
            <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>
              Sends via Resend with <span className="mono">[TEST]</span> prefix on the subject.
              Uses your current preview variable values.
            </p>
          </div>
        </div>

        {/* Pane 3 — Variables */}
        {varsOpen ? (
          <div className="tpl-pane tpl-vars">
            <div className="tpl-vars__head">
              <h4 className="tpl-pane__title">Variables</h4>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => persistVarsOpen(false)}
                title="Hide variables sidebar"
              >
                Hide
              </button>
            </div>
            <p className="muted" style={{ fontSize: 11, marginTop: 0 }}>
              Click a chip to insert <span className="mono">{'{{name}}'}</span> at your cursor.
            </p>
            {(Object.keys(NOTIFICATION_VARIABLE_GROUPS) as Array<keyof typeof NOTIFICATION_VARIABLE_GROUPS>).map((group) => (
              <div key={group} className="tpl-vars__group">
                <div className="tpl-vars__group-label">{VARIABLE_GROUP_LABELS[group]}</div>
                <div className="tpl-vars__chips">
                  {NOTIFICATION_VARIABLE_GROUPS[group].map((v) => (
                    <button
                      key={v}
                      type="button"
                      className="tpl-variable-pill"
                      onClick={() => insertAtCursor(`{{${v}}}`)}
                      title={`Insert {{${v}}}`}
                    >
                      <span>{VARIABLE_PRETTY_LABEL[v] ?? v}</span>
                      <span className="mono">{`{{${v}}}`}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="tpl-vars__group">
              <div className="tpl-vars__group-label">Preview values</div>
              <p className="muted" style={{ fontSize: 11, marginTop: 0 }}>
                Overrides only affect the preview, not the saved template.
              </p>
              <div className="tpl-vars__values">
                {Object.entries(previewVars).map(([k, v]) => (
                  <div key={k} className="tpl-vars__value">
                    <span className="mono" style={{ fontSize: 10 }}>{k}</span>
                    <input
                      type="text"
                      value={v}
                      onChange={(e) =>
                        setPreviewVars((prev) => ({ ...prev, [k]: e.target.value }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="tpl-vars-handle"
            onClick={() => persistVarsOpen(true)}
            title="Show variables sidebar"
          >
            ‹ Variables
          </button>
        )}
      </div>
    </div>
  );
}

// ── Template parser (mirror of server-side renderTemplate) ─────────────────
//
// Used for the live preview. Same syntax as the server. Two modes:
//  - 'text': render { {var} } → vars[name] (empty string if unknown), buttons
//            → "[Label] (url)". Used for subject + the textual fallback.
//  - 'html': render with escaped HTML, button → real <a>, missing vars
//            → red-tinted raw token chip.

function escapeHtml(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function renderPreview(
  template: string,
  vars: Record<string, string>,
  mode: 'text' | 'html',
): { rendered: string } {
  if (mode === 'text') {
    let out = template.replace(
      /\{\{\s*button:([^:|{}]+):([a-z_][a-z0-9_]*)\s*\}\}/gi,
      (_m, label, urlVar) => {
        const url = vars[urlVar] ?? '';
        return `[${label.trim()}] (${url})`;
      },
    );
    out = out.replace(
      /\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/gi,
      (_m, name) => vars[name] ?? '',
    );
    return { rendered: out };
  }
  const escaped = escapeHtml(template);
  let out = escaped.replace(
    /\{\{\s*button:([^:|{}]+):([a-z_][a-z0-9_]*)\s*\}\}/gi,
    (_m, label, urlVar) => {
      const url = vars[urlVar] ?? '';
      return `<a href="${escapeHtml(url)}" class="tpl-button">${escapeHtml(String(label).trim())}</a>`;
    },
  );
  out = out.replace(
    /\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/gi,
    (_m, name) => {
      if (Object.prototype.hasOwnProperty.call(vars, name)) {
        const v = vars[name] ?? '';
        return `<span class="tpl-chip tpl-chip--resolved" title="{{${escapeHtml(name)}}}">${escapeHtml(v)}</span>`;
      }
      return `<span class="tpl-chip tpl-chip--missing" title="Unknown variable">{{${escapeHtml(name)}}}</span>`;
    },
  );
  const paragraphs = out
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replaceAll('\n', '<br>')}</p>`)
    .join('');
  return { rendered: paragraphs };
}

// Audit log uses the same eventVerb + eventCategory taxonomy as the
// request-detail HistoryTimeline so the two views can't visually drift.
// Server already sorts DESC (newest first), which matches the new
// platform-wide direction. Adds client-side category filter pills.
const CATEGORY_LABEL: Record<EventCategory, string> = {
  submission: 'Submissions',
  reviewer_action: 'Reviewer actions',
  clarification_requested: 'Clarifications requested',
  clarification_responded: 'Submitter responses',
  workflow_transition: 'Workflow transitions',
  ai_generation: 'AI runs',
  audit_meta: 'Admin / system',
};

const CATEGORY_DOT: Record<EventCategory, string> = {
  submission: 'submitted',
  reviewer_action: 'approved',
  clarification_requested: 'rollback',
  clarification_responded: 'responded',
  workflow_transition: 'system',
  ai_generation: 'ai',
  audit_meta: 'system',
};

function AuditLogTab() {
  const navigate = useNavigate();
  const log = useQuery({ queryKey: ['admin-audit'], queryFn: () => api.adminAuditLog(40) });
  const [activeCategories, setActiveCategories] = useState<Set<EventCategory>>(
    () => new Set(EVENT_CATEGORIES),
  );

  function toggleCategory(c: EventCategory): void {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      // If user clears everything, snap back to "all on" to avoid an empty view trap.
      return next.size === 0 ? new Set(EVENT_CATEGORIES) : next;
    });
  }

  const entries = log.data ?? [];
  const filtered = entries.filter((e) =>
    activeCategories.has(eventCategory(e.eventType as EventType)),
  );
  const counts = new Map<EventCategory, number>();
  for (const e of entries) {
    const c = eventCategory(e.eventType as EventType);
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }

  return (
    <div className="card reveal reveal-2">
      <div className="card__header">
        <h3 className="card__title">System-wide audit log</h3>
        <span className="muted" style={{ fontSize: 11 }}>Append-only · last 40 events · newest first</span>
      </div>

      {/* Category filter pills — toggle which categories render. */}
      <div
        style={{
          padding: '12px 16px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        {EVENT_CATEGORIES.map((c) => {
          const isOn = activeCategories.has(c);
          const n = counts.get(c) ?? 0;
          return (
            <button
              key={c}
              type="button"
              onClick={() => toggleCategory(c)}
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                border: '1px solid var(--border-subtle)',
                background: isOn ? 'var(--color-navy)' : 'transparent',
                color: isOn ? 'var(--color-white)' : 'var(--color-steel)',
                fontSize: 11,
                cursor: 'pointer',
                opacity: n === 0 ? 0.4 : 1,
              }}
              disabled={n === 0}
            >
              {CATEGORY_LABEL[c]} {n > 0 && `· ${n}`}
            </button>
          );
        })}
      </div>

      <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
        {log.isLoading && <p className="muted" style={{ padding: 18 }}>Loading…</p>}
        {!log.isLoading && filtered.length === 0 && (
          <p className="muted" style={{ padding: 18 }}>No events match the active filter.</p>
        )}
        {filtered.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 36 }} />
                <th style={{ width: 180 }}>When</th>
                <th style={{ width: 140 }}>Ticket</th>
                <th style={{ width: 220 }}>Actor</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const cat = eventCategory(e.eventType as EventType);
                return (
                  <tr
                    key={e.id}
                    onClick={() => navigate(`/requests/${e.requestId}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <span
                        className={`timeline__dot timeline__dot--${CATEGORY_DOT[cat]}`}
                        style={{ position: 'static', display: 'inline-block' }}
                        title={CATEGORY_LABEL[cat]}
                        aria-hidden="true"
                      />
                    </td>
                    <td>
                      <span className="muted mono" style={{ fontSize: 11 }}>
                        {formatRelative(e.createdAt)}
                      </span>
                    </td>
                    <td>
                      <span className="mono">{e.ticketNumber}</span>
                    </td>
                    <td>
                      {e.actorName}{' '}
                      <span className="muted" style={{ fontSize: 11 }}>({e.actorRole})</span>
                    </td>
                    <td>
                      {eventVerb(e.eventType as EventType)}
                      {e.comment && (
                        <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>
                          — {truncate(e.comment, 80)}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max) + '…';
}

// AI Prompt tab — view + edit the system prompts that drive the two AI layers
// of the vendor-intel research pipeline. The server appends a fixed JSON-schema
// footer per layer that's NOT editable here so admin tweaks can't break parsing.
function AiPromptTab() {
  const [layer, setLayer] = useState<'evidence' | 'threat'>('evidence');
  return (
    <div className="reveal reveal-2" style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1000 }}>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-subtle)' }}>
        {(['evidence', 'threat'] as const).map((l) => {
          const isActive = layer === l;
          return (
            <button
              key={l}
              type="button"
              onClick={() => setLayer(l)}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--color-navy)' : '2px solid transparent',
                fontWeight: isActive ? 700 : 500,
                color: isActive ? 'var(--color-navy)' : 'var(--color-steel)',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {l === 'evidence'
                ? 'Layer 1 — Evidence prompt'
                : 'Layer 2 — Threat prompt'}
            </button>
          );
        })}
      </div>
      <LayerPromptEditor layer={layer} />
    </div>
  );
}

function LayerPromptEditor({ layer }: Readonly<{ layer: 'evidence' | 'threat' }>) {
  const qc = useQueryClient();
  const toasts = useToasts();
  const promptQuery = useQuery({
    queryKey: ['admin-intel-prompt', layer],
    queryFn: () => api.adminGetLayerPrompt(layer),
  });
  const [draft, setDraft] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (text: string) => api.adminSetLayerPrompt(layer, text),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-intel-prompt', layer] });
      setDraft(null);
      toasts.push(`Layer ${layer === 'evidence' ? '1' : '2'} prompt saved.`);
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Could not save prompt.', 'err');
    },
  });

  const reset = useMutation({
    mutationFn: () => api.adminResetLayerPrompt(layer),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-intel-prompt', layer] });
      setDraft(null);
      toasts.push(`Layer ${layer === 'evidence' ? '1' : '2'} prompt reset to default.`);
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Could not reset prompt.', 'err');
    },
  });

  if (promptQuery.isLoading) return <p className="muted">Loading prompt…</p>;
  if (promptQuery.error || !promptQuery.data) {
    return <p className="field__error">Could not load this layer's prompt.</p>;
  }

  const current = promptQuery.data.prompt;
  const value = draft ?? current;
  const dirty = draft !== null && draft !== current;
  const chars = value.length;
  const minChars = 50;
  const maxChars = 40_000;
  const tooShort = chars < minChars;
  const tooLong = chars > maxChars;

  return (
    <div className="card card--padded">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <h3 className="card__title" style={{ margin: 0 }}>
          {layer === 'evidence' ? 'Layer 1 — Evidence extraction prompt' : 'Layer 2 — Threat modeling prompt'}
        </h3>
        <span className="muted" style={{ fontSize: 11 }}>
          {promptQuery.data.isDefault ? 'Currently: default' : 'Currently: customized'}
        </span>
      </div>
      <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>
        {layer === 'evidence'
          ? 'Sent to the Layer 1 model on every "Regenerate". This layer extracts verifiable facts only — no severity, no recommendations. The model receives the vendor name and produces an EvidencePayload (a fixed JSON-schema footer is appended automatically).'
          : 'Sent to the Layer 2 model on every "Regenerate". This layer receives the Layer 1 evidence payload as input and produces threat findings with severity *candidates* (advisory only — the deterministic rule engine picks the final severity).'}
      </p>

      <div className="field" style={{ marginBottom: 8 }}>
        <label className="field__label" htmlFor={`ai-prompt-textarea-${layer}`}>
          Prompt body
        </label>
        <textarea
          id={`ai-prompt-textarea-${layer}`}
          value={value}
          onChange={(e) => setDraft(e.target.value)}
          rows={28}
          spellCheck={false}
          style={{
            width: '100%',
            fontFamily: 'var(--font-mono, monospace)',
            fontSize: 12,
            lineHeight: 1.5,
            padding: 12,
            border: '1px solid var(--border-subtle)',
            borderRadius: 8,
            background: '#FBFCFD',
            color: 'var(--color-navy)',
            resize: 'vertical',
            minHeight: 320,
          }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: 11,
            color: tooShort || tooLong ? 'var(--color-red)' : 'var(--color-steel)',
          }}
        >
          {chars.toLocaleString()} chars
          {tooShort && ` · minimum ${minChars}`}
          {tooLong && ` · maximum ${maxChars}`}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn--ghost btn--sm"
            disabled={save.isPending || reset.isPending}
            onClick={() => setDraft(null)}
          >
            Discard changes
          </button>
          <button
            className="btn btn--secondary btn--sm"
            disabled={save.isPending || reset.isPending || promptQuery.data.isDefault}
            onClick={() => {
              if (window.confirm('Reset this layer\'s prompt to the built-in default? Your current text will be overwritten.')) {
                reset.mutate();
              }
            }}
            title="Restore the default prompt for this layer"
          >
            {reset.isPending ? 'Resetting…' : 'Reset to default'}
          </button>
          <button
            className="btn btn--primary btn--sm"
            disabled={save.isPending || !dirty || tooShort || tooLong}
            onClick={() => save.mutate(value)}
          >
            {save.isPending ? 'Saving…' : 'Save prompt'}
          </button>
        </div>
      </div>

      <p style={{ fontSize: 11, color: 'var(--color-steel)', marginTop: 12, marginBottom: 0 }}>
        Changes take effect immediately for the next "Regenerate". To re-run an existing vendor
        under the new prompt, open the request and click ↻ Regenerate on the Company Background card.
      </p>
    </div>
  );
}

